import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { TenantMembersAdmin } from "@/components/tenant-members-admin";
import { useAuthStore } from "@/stores/authStore";
import { fetchTenantInvites, fetchTenantMembers } from "@/services/api/instructor";

export function TenantAdminMembersPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const t = useTranslations("admin");
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const adminQuery = useQuery({
    queryKey: ["tenant-admin-members", tenantSlug],
    queryFn: async () => {
      const [members, invites] = await Promise.all([
        fetchTenantMembers(),
        fetchTenantInvites(tenantSlug),
      ]);
      return { members, invites };
    },
    enabled: isAuthenticated && user?.role === "tenant_admin",
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (user?.role !== "tenant_admin") {
    return <Navigate to={`/t/${tenantSlug}/dashboard`} replace />;
  }

  if (adminQuery.isLoading) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10" data-testid="tenant-admin-members-loading">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </main>
    );
  }

  if (adminQuery.isError) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10" data-testid="tenant-admin-members-error">
        <p className="text-sm text-red-600" role="alert">
          {t("loadFailed")}
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10" data-testid="tenant-admin-members">
      <h1 className="text-2xl font-semibold">{t("membersTitle")}</h1>
      <p className="mt-2 text-sm text-[var(--text-muted)]">
        {t("membersSubtitle", { tenant: tenantSlug })}
      </p>
      <div className="mt-8">
        <TenantMembersAdmin
          tenantSlug={tenantSlug}
          currentUserId={user.id}
          initialMembers={adminQuery.data?.members ?? []}
          initialInvites={adminQuery.data?.invites ?? []}
        />
      </div>
    </main>
  );
}
